// Predictive search drawer (slice-10).
// Web component that hooks into snippets/predictive-search-drawer.liquid.
//
// Flow:
//   1. Click [data-search-trigger] → drawer.showModal(), input focused.
//   2. Input event → 250ms debounce → fetch '?section_id=predictive-search&q=<terms>'.
//      AbortController cancels in-flight on rapid typing.
//   3. Section Rendering API response → strip .shopify-section wrapper →
//      inject inner .predictive-results into drawer body. data-total-count
//      drives footer visibility + live-region announce.
//   4. Esc / backdrop-click / close-button → drawer.close(),
//      focus returned to trigger.
//
// i18n read from inline <script type="application/json" data-predictive-search-i18n>
// in the snippet. __TERMS__ / __COUNT__ placeholders are substituted at render.

class PredictiveSearchDrawer extends HTMLElement {
  static MIN_QUERY = 2;
  static DEBOUNCE_MS = 250;
  static SECTION_ID = 'predictive-search';
  // Shopify only populates the `predictive_search` Liquid global when the
  // request hits /search/suggest with section_id + resources[type]. Hitting
  // the current pathname returns 200 but an empty resources object.
  static ENDPOINT = `${window.Shopify.routes.root}search/suggest`;
  static PRODUCT_LIMIT = 6;
  static ARTICLE_LIMIT = 4;

  connectedCallback() {
    this.dialog       = this.querySelector('dialog');
    this.input        = this.querySelector('#predictive-search-input');
    this.results      = this.querySelector('[data-predictive-search-results]');
    this.footer       = this.querySelector('[data-predictive-search-footer]');
    this.viewAll      = this.querySelector('[data-predictive-search-view-all]');
    this.viewAllLabel = this.querySelector('[data-view-all-label]');
    this.liveRegion   = this.querySelector('[data-predictive-search-live]');
    this.closeBtn     = this.querySelector('[data-predictive-search-close]');

    const i18nNode = this.querySelector('[data-predictive-search-i18n]');
    this.i18n = i18nNode ? JSON.parse(i18nNode.textContent) : {};

    this._debounceTimer = null;
    this._abortController = null;
    this._lastQuery = '';
    this._lastTrigger = null;

    document.querySelectorAll('[data-search-trigger]').forEach((btn) =>
      btn.addEventListener('click', (e) => { e.preventDefault(); this.open(btn); })
    );

    this.closeBtn.addEventListener('click', () => this.close());
    this.dialog.addEventListener('click', this._onBackdropClick);
    this.dialog.addEventListener('cancel', this._onCancel);
    this.input.addEventListener('input', this._onInput);
  }

  open(trigger) {
    if (this.dialog.open) return;
    this._lastTrigger = trigger || null;
    this.dialog.showModal();
    this.input.setAttribute('aria-expanded', 'true');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    requestAnimationFrame(() => this.input.focus());
  }

  close() {
    if (!this.dialog.open) return;
    this.dialog.close();
    this.input.setAttribute('aria-expanded', 'false');
    if (this._lastTrigger) {
      this._lastTrigger.setAttribute('aria-expanded', 'false');
      this._lastTrigger.focus();
      this._lastTrigger = null;
    }
    this._lastQuery = '';
    this.input.value = '';
    this._renderEmpty();
  }

  _onInput = () => {
    clearTimeout(this._debounceTimer);
    const q = this.input.value.trim();
    if (q.length < PredictiveSearchDrawer.MIN_QUERY) {
      this._renderEmpty();
      return;
    }
    this._debounceTimer = setTimeout(() => this._fetch(q), PredictiveSearchDrawer.DEBOUNCE_MS);
  };

  async _fetch(q) {
    if (q === this._lastQuery) return;
    this._lastQuery = q;
    if (this._abortController) this._abortController.abort();
    this._abortController = new AbortController();
    this.results.setAttribute('aria-busy', 'true');

    try {
      const params = new URLSearchParams({
        q,
        section_id: PredictiveSearchDrawer.SECTION_ID,
        'resources[type]': 'product,article',
        'resources[limit]': String(Math.max(PredictiveSearchDrawer.PRODUCT_LIMIT, PredictiveSearchDrawer.ARTICLE_LIMIT)),
        'resources[limit_scope]': 'each',
      });
      const url = `${PredictiveSearchDrawer.ENDPOINT}?${params.toString()}`;
      const res = await fetch(url, { signal: this._abortController.signal });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const inner = doc.querySelector('.predictive-results') || doc.body;
      // Wrap injected results in the shared inner-container so they share
      // the centered max-width of the header / footer chrome.
      this.results.innerHTML = `<div class="predictive-search__inner">${inner.outerHTML}</div>`;
      this._updateFooter(q, inner);
      this._announce(inner);
    } catch (err) {
      if (err.name === 'AbortError') return;
      this._renderError();
    } finally {
      this.results.setAttribute('aria-busy', 'false');
    }
  }

  _updateFooter(q, inner) {
    const count = parseInt(inner.dataset.totalCount || '0', 10);
    if (count > 0) {
      this.footer.hidden = false;
      // viewAll.href in snippet defaults to routes.search_url — preserve path, swap querystring
      const base = (this.viewAll.getAttribute('href') || `${window.Shopify.routes.root}search`).split('?')[0];
      this.viewAll.href = `${base}?q=${encodeURIComponent(q)}`;
      this.viewAllLabel.textContent = (this.i18n.view_all_html || '').replace('__TERMS__', q);
    } else {
      this.footer.hidden = true;
    }
  }

  _announce(inner) {
    const count = parseInt(inner.dataset.totalCount || '0', 10);
    this.liveRegion.textContent = (this.i18n.results_for || '').replace('__COUNT__', String(count));
  }

  _renderEmpty() {
    // Reset all in-flight + cached state so a typed-then-deleted-then-retyped
    // query refetches cleanly instead of hitting the q === _lastQuery
    // early-return in _fetch or having a stale response repopulate stale results.
    if (this._abortController) {
      this._abortController.abort();
      this._abortController = null;
    }
    this._lastQuery = '';
    this.results.innerHTML = `<div class="predictive-search__inner"><p class="predictive-search__empty">${this._escape(this.i18n.empty || '')}</p></div>`;
    this.footer.hidden = true;
    this.liveRegion.textContent = '';
    this.results.setAttribute('aria-busy', 'false');
  }

  _renderError() {
    this.results.innerHTML = `<div class="predictive-search__inner"><p class="predictive-search__error">${this._escape(this.i18n.error || '')}</p></div>`;
    this.footer.hidden = true;
  }

  _escape(s) {
    const div = document.createElement('div');
    div.textContent = s;
    return div.innerHTML;
  }

  _onBackdropClick = (e) => {
    const rect = this.dialog.getBoundingClientRect();
    if (
      e.clientY < rect.top || e.clientY > rect.bottom ||
      e.clientX < rect.left || e.clientX > rect.right
    ) {
      this.close();
    }
  };

  _onCancel = (e) => {
    e.preventDefault();
    this.close();
  };
}

customElements.define('predictive-search-drawer', PredictiveSearchDrawer);
